import type { PrimitiveDef, RecipeDef, SubstrateManifest } from '../manifest';
import type { BenchEdge, BenchGraph, BenchNode } from './types';

const ACCEPTANCE_CHAIN = ['source.ingest', 'codex.chunk_get', 'llm.complete', 'approval.gate', 'sink.publish'];

function nowIso(): string {
  return new Date().toISOString();
}

function primitiveById(manifest: SubstrateManifest, id: string): PrimitiveDef {
  const primitive = manifest.primitives.find((candidate) => candidate.id === id);
  if (!primitive) {
    throw new Error(`Unknown primitive in bench recipe: ${id}`);
  }
  return primitive;
}

function defaultValueForSchemaProperty(schema: unknown): unknown {
  if (!schema || typeof schema !== 'object') return '';
  const property = schema as { default?: unknown; enum?: unknown[]; type?: string };
  if (property.default !== undefined) return property.default;
  if (Array.isArray(property.enum) && property.enum.length > 0) return property.enum[0];
  if (property.type === 'number' || property.type === 'integer') return 1;
  if (property.type === 'boolean') return false;
  if (property.type === 'array') return [];
  if (property.type === 'object') return {};
  return '';
}

export function defaultParametersForPrimitive(primitive: PrimitiveDef): Record<string, unknown> {
  const parameterSchema = primitive.parameter_schema;
  if (!parameterSchema || typeof parameterSchema !== 'object') return {};

  const properties = (parameterSchema as { properties?: Record<string, unknown> }).properties ?? {};
  return Object.fromEntries(
    Object.entries(properties).map(([key, schema]) => [key, defaultValueForSchemaProperty(schema)])
  );
}

export function createGraphFromRecipe(manifest: SubstrateManifest, recipe?: RecipeDef): BenchGraph {
  const sourceRecipe = recipe ?? manifest.default_recipes[0];
  const primitiveIds = sourceRecipe?.primitive_ids ?? ACCEPTANCE_CHAIN;
  const nodes: BenchNode[] = primitiveIds.map((primitiveId, index) => {
    const primitive = primitiveById(manifest, primitiveId);
    return {
      id: `node-${primitive.id.replaceAll('.', '-')}-${index + 1}`,
      primitiveId: primitive.id,
      label: primitive.label,
      x: 32 + index * 210,
      y: 120,
      parameters: defaultParametersForPrimitive(primitive),
    };
  });

  const edges: BenchEdge[] = nodes.slice(0, -1).map((node, index) => ({
    id: `edge-${node.id}-${nodes[index + 1].id}`,
    from: node.id,
    to: nodes[index + 1].id,
  }));

  return {
    id: sourceRecipe?.id ?? 'source-to-approved-publish',
    label: sourceRecipe?.label ?? 'Source -> Codex -> LLM -> Approval -> Sink',
    nodes,
    edges,
    updatedAt: nowIso(),
  };
}

export function addPrimitiveToGraph(graph: BenchGraph, primitive: PrimitiveDef): BenchGraph {
  const nextIndex = graph.nodes.length + 1;
  const node: BenchNode = {
    id: `node-${primitive.id.replaceAll('.', '-')}-${nextIndex}`,
    primitiveId: primitive.id,
    label: primitive.label,
    x: 32 + graph.nodes.length * 210,
    y: graph.nodes.length % 2 === 0 ? 120 : 280,
    parameters: defaultParametersForPrimitive(primitive),
  };
  const previous = graph.nodes[graph.nodes.length - 1];

  return {
    ...graph,
    nodes: [...graph.nodes, node],
    edges: previous
      ? [...graph.edges, { id: `edge-${previous.id}-${node.id}`, from: previous.id, to: node.id }]
      : graph.edges,
    updatedAt: nowIso(),
  };
}

export function updateNodeParameters(
  graph: BenchGraph,
  nodeId: string,
  parameters: Record<string, unknown>
): BenchGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => (node.id === nodeId ? { ...node, parameters } : node)),
    updatedAt: nowIso(),
  };
}

export function orderedNodes(graph: BenchGraph): BenchNode[] {
  if (graph.nodes.length <= 1) return graph.nodes;
  const targets = new Set(graph.edges.map((edge) => edge.to));
  const first = graph.nodes.find((node) => !targets.has(node.id)) ?? graph.nodes[0];
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const result: BenchNode[] = [];
  let cursor: BenchNode | undefined = first;

  while (cursor && !result.some((node) => node.id === cursor?.id)) {
    result.push(cursor);
    const nextEdge = graph.edges.find((edge) => edge.from === cursor?.id);
    cursor = nextEdge ? byId.get(nextEdge.to) : undefined;
  }

  return result.length === graph.nodes.length ? result : graph.nodes;
}

export function graphPrimitiveIds(graph: BenchGraph): string[] {
  return orderedNodes(graph).map((node) => node.primitiveId);
}

export { ACCEPTANCE_CHAIN };
